import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  AdminFormActionTypes,
  AdminFormLoad,
  AdminFormLoadFail,
  AdminFormLoadSuccess,
  AdminFormsListLoad,
  AdminFormsListLoadSuccess,
  AdminFormsListLoadFail,
  AdminFormCreate,
  AdminFormCreateSuccess,
  AdminFormUpdate,
  AdminFormUpdateSuccess,
  AdminFormDelete,
  AdminFormDeleteSuccess,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { AdminFormDto, AdminFormListItemDto, PaginatedResponse } from '@completion/models';
import { AdminFormService } from '../../services/admin-form.service';
import { trackResource } from './resource-fetch.util';
import { NotificationService } from '@completion/services';

@Injectable()
export class AdminFormEffect {
  constructor(
    private actions$: Actions,
    private adminFormService: AdminFormService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormLoad>(AdminFormActionTypes.AdminFormLoad),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'getAdminForm',
        serviceArgs: (action) => [action.id],
        onSuccess: (response) => new AdminFormLoadSuccess(response),
        onError: (error) => new AdminFormLoadFail(error),
      }),
    ),
  );

  loadAdminForms$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormsListLoad>(AdminFormActionTypes.AdminFormsListLoad),
      trackResource<PaginatedResponse<AdminFormListItemDto>>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'getAdminForms',
        serviceArgs: (action) => [action.params],
        onSuccess: (response) => new AdminFormsListLoadSuccess(response),
        onError: (error) => new AdminFormsListLoadFail(error),
      }),
    ),
  );

  createAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormCreate>(AdminFormActionTypes.AdminFormCreate),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'createAdminForm',
        serviceArgs: (action) => [action.form],
        onSuccess: (response) => new AdminFormCreateSuccess(response),
      }),
    ),
  );

  updateAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormUpdate>(AdminFormActionTypes.AdminFormUpdate),
      trackResource<AdminFormDto>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'updateAdminForm',
        serviceArgs: (action) => [action.form],
        onSuccess: (response) => new AdminFormUpdateSuccess(response),
      }),
    ),
  );

  deleteAdminForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AdminFormDelete>(AdminFormActionTypes.AdminFormDelete),
      trackResource<void>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.adminFormService,
        method: 'deleteAdminForm',
        serviceArgs: (action) => [action.form.id],
        onSuccess: () => new AdminFormDeleteSuccess(),
      }),
    ),
  );
}
